// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@rpsc/App/Components/Notify";
import { xHasProductsWithInvalidPrice } from "@rpsc/App/Store/Selectors/CartSelectors";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

// XNG-Change
const mapStateToProps = (state: ApplicationState) => {
    const cart = getCurrentCartState(state).value;
    return {
        cart,
    };
};

export interface CartApproverMessageStyles {
    approvingText?: TypographyPresentationProps;
    approvingWrapper?: InjectableCss;
}
export const cartApproverMessageStyles: CartApproverMessageStyles = {
    approvingWrapper: {
        css: css`
            text-align: center;
            padding: 10px;
            background-color: ${getColor("info.main")};
        `,
    },
    approvingText: {
        css: css`
            width: 100%;
        `,
    },
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

const styles = cartApproverMessageStyles;
const CartApproverMessage = ({ cart }: Props) => {
    if (!cart || cart.cartLines?.length === 0) {
        return null;
    }
    // XNG-Change
    const cartLines = cart.cartLines || [];
    const productsCannotBePurchased = cartLines.some(o => o.isRestricted || !o.isActive || o.cantBuy);
    const cartNotPriced = cart.cartNotPriced;

    const hasInsufficientInventory =
        cart?.hasInsufficientInventory && cart?.fulfillmentMethod === FulfillmentMethod.PickUp;

    const hasInvalidPrice = xHasProductsWithInvalidPrice(cart);
    const failedToGetRealTimeInventory = cart.failedToGetRealTimeInventory;
    // XNG-Change
    const isQuoteRequired = cart?.cartLines?.some(cartLine => cartLine.quoteRequired);

    // XNG-Change
    return (
        <StyledWrapper {...styles.approvingWrapper}>
            {cart.isAwaitingApproval && (
                <Notify type="warning" {...styles.approvingText} data-test-selector="cart_approvingOrderMessage">
                    {translate("Approving Order for {0}", cart.initiatedByUserName?.toUpperCase())}
                </Notify>
            )}
            {productsCannotBePurchased && (
                <Notify
                    type="warning"
                    showClose={false}
                    fullWidth={true}
                    data-test-selector="cartlineHeader_warningProductsCannotBePurchased"
                >
                    {siteMessage("Cart_ProductsCannotBePurchased")}
                </Notify>
            )}
            {cartNotPriced && (
                <Notify
                    showClose={false}
                    type="warning"
                    fullWidth={true}
                    data-test-selector="cartlineHeader_NoPriceAvailableAtCheckout"
                >
                    {siteMessage("Cart_NoPriceAvailableAtCheckout")}
                </Notify>
            )}
            {hasInsufficientInventory && (
                <Notify
                    showClose={false}
                    type="warning"
                    fullWidth={true}
                    data-test-selector="cartlineHeader_warningInsufficientInventoryAtCheckout"
                >
                    {siteMessage("Cart_InsufficientInventoryAtCheckout")}
                </Notify>
            )}
            {/* consider invalid price as quote required */}
            {/* {!cartNotPriced && hasInvalidPrice && (
                <Notify showClose={false} type="warning" fullWidth={true}>
                    {siteMessage("Cart_InvalidPriceAtCheckout")}
                </Notify>
            )} */}
            {failedToGetRealTimeInventory && (
                <Notify showClose={false} type="warning" fullWidth={true}>
                    {siteMessage("Cart_NoInventoryAvailableAtCheckout")}
                </Notify>
            )}

            {/* // XNG-Change */}
            {(isQuoteRequired || (!cartNotPriced && hasInvalidPrice)) && (
                <Notify showClose={false} type="warning" fullWidth={true}>
                    {siteMessage("Custom_Cart_Quote_Required")}
                </Notify>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CartApproverMessage),
    definition: {
        displayName: "Cart Approver Message",
        icon: "cart-circle-check",
        group: "Common",
    },
};

export default widgetModule;
